/**
 * useRealtimeAdmin — Supabase Realtime Engine for Wonder Herbals Admin
 * Subscribes to live database events (Orders, Inventory, Customer messages)
 * with instant UI updates, audio chimes, and React Query cache invalidation.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { playOrderChime, playAlertChime } from '../lib/sound';
import { toast } from 'sonner';
import { formatINR } from '@wonderherbals/shared';

export function useRealtimeAdmin() {
  const queryClient = useQueryClient();
  const isSubscribedRef = useRef(false);

  useEffect(() => {
    if (isSubscribedRef.current) return;
    isSubscribedRef.current = true;

    // 1. Orders Channel
    const ordersChannel = supabase
      .channel('admin-realtime-orders')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        (payload) => {
          const newOrder = payload.new as any;
          const orderNum = newOrder.order_number || `WH-${newOrder.id?.slice(0, 8)}`;
          const amount = formatINR(newOrder.total || 0);

          playOrderChime();
          toast.success(`🛍️ New Order ${orderNum} received! (${amount})`, {
            description: `${newOrder.shipping_address?.fullName || 'Customer'} placed an order.`,
            duration: 6000,
          });

          // Invalidate relevant queries
          queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
          queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
          queryClient.invalidateQueries({ queryKey: ['admin', 'analytics'] });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders' },
        (payload) => {
          const updated = payload.new as any;
          queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
          queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
          queryClient.invalidateQueries({ queryKey: ['admin-order', updated.id] });
        }
      )
      .subscribe();

    // 2. Products & Inventory Channel
    const productsChannel = supabase
      .channel('admin-realtime-products')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'products' },
        (payload) => {
          const prod = payload.new as any;
          const inventory = prod.inventory_count ?? prod.total_inventory;

          if (inventory !== undefined && inventory <= 5 && inventory > 0) {
            playAlertChime();
            toast.warning(`⚠️ Low Stock Alert: ${prod.name}`, {
              description: `Only ${inventory} units left in stock.`,
            });
          }

          queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
          queryClient.invalidateQueries({ queryKey: ['admin', 'product', prod.id] });
          queryClient.invalidateQueries({ queryKey: ['admin', 'products', 'inventory'] });
        }
      )
      .subscribe();

    // 3. Customer Chat Messages
    const chatChannel = supabase
      .channel('admin-realtime-chat')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => {
          const msg = payload.new as any;
          if (msg.role === 'customer') {
            toast.info(`💬 New message from customer`, {
              description: msg.body?.substring(0, 60),
            });
            queryClient.invalidateQueries({ queryKey: ['admin-chat-sessions'] });
            queryClient.invalidateQueries({ queryKey: ['admin-chat-messages', msg.session_id] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ordersChannel);
      supabase.removeChannel(productsChannel);
      supabase.removeChannel(chatChannel);
      isSubscribedRef.current = false;
    };
  }, [queryClient]);
}
